/**
 * Inlined SVG icon components replacing:
 *   - `CameraFilled` from `@ant-design/icons`
 *   - `CheckIcon`, `ExclamationTriangleIcon` from `@radix-ui/react-icons`
 *   - `ArrowLeftIcon`, `ArrowRightIcon` from `@radix-ui/react-icons`
 *
 * SVG path data extracted directly from the respective packages.
 */
import React from 'react'

// ---------------------------------------------------------------------------
// Shared prop types
// ---------------------------------------------------------------------------

interface IconProps extends React.SVGAttributes<SVGElement> {
  color?: string
}

// ---------------------------------------------------------------------------
// CameraFilled (from @ant-design/icons, viewBox 64 64 896 896)
// ---------------------------------------------------------------------------

export const CameraFilledIcon: React.FC<IconProps> = ({
  color = 'currentColor',
  style,
  ...props
}) => (
  <svg
    viewBox='64 64 896 896'
    focusable='false'
    fill={color}
    width='1em'
    height='1em'
    style={style}
    {...props}>
    <path d='M864 260H728l-32.4-90.8a32.07 32.07 0 00-30.2-21.2H358.6c-13.5 0-25.6 8.5-30.1 21.2L296 260H160c-44.2 0-80 35.8-80 80v456c0 44.2 35.8 80 80 80h704c44.2 0 80-35.8 80-80V340c0-44.2-35.8-80-80-80zM512 716c-88.4 0-160-71.6-160-160s71.6-160 160-160 160 71.6 160 160-71.6 160-160 160zm-96-160a96 96 0 10192 0 96 96 0 10-192 0z' />
  </svg>
)

// ---------------------------------------------------------------------------
// CheckIcon (from @radix-ui/react-icons, viewBox 0 0 15 15)
// ---------------------------------------------------------------------------

export const CheckIcon: React.FC<IconProps> = ({
  color = 'currentColor',
  ...props
}) => (
  <svg
    width='15'
    height='15'
    viewBox='0 0 15 15'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    {...props}>
    <path
      d='M11.4669 3.72684C11.7558 3.91574 11.8369 4.30308 11.648 4.59198L7.39799 11.092C7.29783 11.2452 7.13556 11.3467 6.95402 11.3699C6.77247 11.3931 6.58989 11.3355 6.45446 11.2124L3.70446 8.71241C3.44905 8.48022 3.43023 8.08494 3.66242 7.82953C3.89461 7.57412 4.28989 7.55529 4.5453 7.78749L6.75292 9.79441L10.6018 3.90792C10.7907 3.61902 11.178 3.53795 11.4669 3.72684Z'
      fill={color}
      fillRule='evenodd'
      clipRule='evenodd'
    />
  </svg>
)

// ---------------------------------------------------------------------------
// ExclamationTriangleIcon (from @radix-ui/react-icons, viewBox 0 0 15 15)
// ---------------------------------------------------------------------------

export const ExclamationTriangleIcon: React.FC<IconProps> = ({
  color = 'currentColor',
  ...props
}) => (
  <svg
    width='15'
    height='15'
    viewBox='0 0 15 15'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    {...props}>
    <path
      d='M8.4449 0.608765C8.0183 -0.107015 6.9817 -0.107015 6.55509 0.608766L0.161178 11.3368C-0.275824 12.07 0.252503 13 1.10608 13H13.8939C14.7475 13 15.2758 12.07 14.8388 11.3368L8.4449 0.608765ZM7.4141 1.12073C7.45288 1.05566 7.54712 1.05566 7.5859 1.12073L13.9798 11.8488C14.0196 11.9154 13.9715 12 13.8939 12H1.10608C1.02849 12 0.980454 11.9154 1.02018 11.8488L7.4141 1.12073ZM6.8269 4.48611C6.81221 4.10423 7.11783 3.78663 7.5 3.78663C7.88217 3.78663 8.18778 4.10423 8.1731 4.48612L8.01921 8.48701C8.00848 8.766 7.7792 8.98664 7.5 8.98664C7.2208 8.98664 6.99151 8.766 6.98078 8.48701L6.8269 4.48611ZM8.24989 10.476C8.24989 10.8902 7.9141 11.226 7.49989 11.226C7.08567 11.226 6.74989 10.8902 6.74989 10.476C6.74989 10.0618 7.08567 9.72599 7.49989 9.72599C7.9141 9.72599 8.24989 10.0618 8.24989 10.476Z'
      fill={color}
      fillRule='evenodd'
      clipRule='evenodd'
    />
  </svg>
)

// ---------------------------------------------------------------------------
// ArrowLeftIcon (from @radix-ui/react-icons, viewBox 0 0 15 15)
// ---------------------------------------------------------------------------

export const ArrowLeftIcon: React.FC<IconProps> = ({
  color = 'currentColor',
  ...props
}) => (
  <svg
    width='15'
    height='15'
    viewBox='0 0 15 15'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    {...props}>
    <path
      d='M6.85355 3.14645C7.04882 3.34171 7.04882 3.65829 6.85355 3.85355L3.70711 7H12.5C12.7761 7 13 7.22386 13 7.5C13 7.77614 12.7761 8 12.5 8H3.70711L6.85355 11.1464C7.04882 11.3417 7.04882 11.6583 6.85355 11.8536C6.65829 12.0488 6.34171 12.0488 6.14645 11.8536L2.14645 7.85355C1.95118 7.65829 1.95118 7.34171 2.14645 7.14645L6.14645 3.14645C6.34171 2.95118 6.65829 2.95118 6.85355 3.14645Z'
      fill={color}
      fillRule='evenodd'
      clipRule='evenodd'
    />
  </svg>
)

// ---------------------------------------------------------------------------
// ArrowRightIcon (from @radix-ui/react-icons, viewBox 0 0 15 15)
// ---------------------------------------------------------------------------

export const ArrowRightIcon: React.FC<IconProps> = ({
  color = 'currentColor',
  ...props
}) => (
  <svg
    width='15'
    height='15'
    viewBox='0 0 15 15'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    {...props}>
    <path
      d='M8.14645 3.14645C8.34171 2.95118 8.65829 2.95118 8.85355 3.14645L12.8536 7.14645C13.0488 7.34171 13.0488 7.65829 12.8536 7.85355L8.85355 11.8536C8.65829 12.0488 8.34171 12.0488 8.14645 11.8536C7.95118 11.6583 7.95118 11.3417 8.14645 11.1464L11.2929 8H2.5C2.22386 8 2 7.77614 2 7.5C2 7.22386 2.22386 7 2.5 7H11.2929L8.14645 3.85355C7.95118 3.65829 7.95118 3.34171 8.14645 3.14645Z'
      fill={color}
      fillRule='evenodd'
      clipRule='evenodd'
    />
  </svg>
)
